import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../lib/firebase';
import { friendlyError } from '../lib/errors';
import { getVerticalTheme, normalizeBusinessType } from '../lib/verticals';

const AuthContext = createContext(null);

async function fetchUserProfile(firebaseUser) {
  try {
    const uidDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (uidDoc.exists()) {
      return { id: uidDoc.id, ...uidDoc.data() };
    }
  } catch (e) {
    console.warn('[auth] users/{uid} no disponible', e?.message);
  }

  try {
    const getMyUserProfile = httpsCallable(functions, 'getMyUserProfile');
    const result = await getMyUserProfile({});
    const user = result?.data?.user;
    if (user) return user;
  } catch (e) {
    console.warn('[auth] getMyUserProfile falló', e?.message);
  }

  return null;
}

async function ensureRucClaim(firebaseUser, userRuc) {
  try {
    await firebaseUser.getIdToken(true);
    const tokenResult = await firebaseUser.getIdTokenResult();
    if (tokenResult?.claims?.ruc) {
      return String(tokenResult.claims.ruc);
    }
    if (userRuc) {
      const setMyClaimFn = httpsCallable(functions, 'setMyClaim');
      await setMyClaimFn({});
      await firebaseUser.getIdToken(true);
      const refreshed = await firebaseUser.getIdTokenResult();
      if (refreshed?.claims?.ruc) {
        return String(refreshed.claims.ruc);
      }
    }
  } catch (e) {
    console.warn('[auth] No se pudo asegurar claim.ruc', e?.message);
  }
  return null;
}

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [ruc, setRuc] = useState(null);
  const [businessType, setBusinessType] = useState('general');
  const [theme, setTheme] = useState(() => getVerticalTheme('general'));
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  const applyCompany = useCallback(async (rucValue) => {
    if (!rucValue) {
      setCompany(null);
      setBusinessType('general');
      setTheme(getVerticalTheme('general'));
      return;
    }
    try {
      const snap = await getDoc(doc(db, 'company', rucValue));
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setCompany(data);
        const bt = normalizeBusinessType(data.businessType);
        setBusinessType(bt);
        setTheme(getVerticalTheme(bt));
      } else {
        setCompany(null);
        setBusinessType('general');
        setTheme(getVerticalTheme('general'));
      }
    } catch (e) {
      console.warn('[auth] No se pudo cargar company', e?.message);
      setCompany(null);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser || !fbUser.emailVerified) {
        if (fbUser && !fbUser.emailVerified) {
          setAuthError({ message: 'Por favor, verifica tu correo electrónico antes de iniciar sesión.' });
          await signOut(auth).catch(() => {});
        }
        setFirebaseUser(null);
        setUser(null);
        setCompany(null);
        setRuc(null);
        setLoading(false);
        return;
      }

      setFirebaseUser(fbUser);
      try {
        const userData = await fetchUserProfile(fbUser);
        if (!userData) {
          setAuthError({ message: 'El usuario no está registrado o no se encuentra activo.' });
          await signOut(auth);
          setUser(null);
          setRuc(null);
          setLoading(false);
          return;
        }
        setUser(userData);
        const claimRuc = await ensureRucClaim(fbUser, userData.ruc);
        const effectiveRuc = claimRuc || userData.ruc || null;
        setRuc(effectiveRuc);
        await applyCompany(effectiveRuc);
      } catch (e) {
        setAuthError({ message: friendlyError(e) });
        setUser(null);
        setRuc(null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [applyCompany]);

  const login = useCallback(async (email, password) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (!cred.user.emailVerified) {
        await signOut(auth);
        setAuthError({ message: 'Por favor, verifica tu correo electrónico antes de iniciar sesión.' });
        return false;
      }
      return true;
    } catch (e) {
      setAuthError({ message: friendlyError(e) });
      return false;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    setFirebaseUser(null);
    setUser(null);
    setCompany(null);
    setRuc(null);
  }, []);

  const clearAuthError = useCallback(() => setAuthError(null), []);

  const value = useMemo(
    () => ({
      firebaseUser,
      user,
      company,
      ruc,
      businessType,
      theme,
      loading,
      authLoading,
      authError,
      login,
      logout,
      clearAuthError,
      isAuthenticated: !!firebaseUser && !!user && !!ruc,
      rol: user?.rol || null,
      plan: user?.plan || user?.subscriptionStatus || null,
    }),
    [firebaseUser, user, company, ruc, businessType, theme, loading, authLoading, authError, login, logout, clearAuthError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
